import { useMemo, useState, type KeyboardEvent } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  id: string;
  name: string;
  /** Optional muted annotation shown after the name (e.g. "(parent)"). */
  suffix?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onSelect: (id: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Fired each time the popover opens — e.g. to lazy-load options on first open. */
  onOpen?: () => void;
  /** Extra classes merged into the trigger button (e.g. to match a compact row's h-8 text-xs). */
  triggerClassName?: string;
}

// 86d39epjt — shared searchable combobox: client-side filter over a cached list +
// arrow-key nav, so long lookups (City/Town, Terms, Customer hierarchy) are typeable
// instead of a giant scroll. Mirrors CustomerCombobox's Popover/Input/list styling.
export function SearchableSelect({
  options,
  value,
  onSelect,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  disabled,
  loading,
  onOpen,
  triggerClassName,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedLabel = useMemo(
    () => options.find((o) => o.id === value)?.name ?? '',
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const commit = (id: string) => {
    onSelect(id);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) commit(opt.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled || loading) return;
        setOpen(next);
        if (!next) setQuery('');
        if (next) {
          setActiveIndex(0);
          onOpen?.();
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate text-left">
            {loading ? 'Loading…' : selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <ul
          role="listbox"
          className="max-h-72 overflow-y-auto py-1"
          // This popover portals outside any parent Dialog, so the Dialog's scroll-lock
          // (react-remove-scroll — a bubble-phase document wheel listener) preventDefaults
          // wheel events here and the list won't scroll. Stop propagation at the scroll
          // container so the lock never sees the event and native scrolling works.
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-muted-foreground">{emptyText}</li>
          ) : (
            filtered.map((opt, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = opt.id === value;
              return (
                <li key={opt.id} role="option" aria-selected={isActive}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => commit(opt.id)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                      isActive && 'bg-accent',
                    )}
                  >
                    <span className="truncate">{opt.name}</span>
                    {opt.suffix && (
                      <span className="shrink-0 text-xs text-muted-foreground">{opt.suffix}</span>
                    )}
                    {isSelected && <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
